import { MutationType } from '@tencent/slide-sdk-pb-model';
import { assert } from 'ts-essentials';
import { SHAPE_DATA_TYPES, DataType } from '../../data';
import { Rule } from '../base/mutation-rules';
import { invert } from '../../utils/protoutil';
import { BaseMutation, type MaybeError } from '../base/base-mutation';
import { ShapeDeltaUtils } from '../base/shape-delta-utils';
import type { PresentationDataModel } from '../../model/presentation-data-model';
import type { IValidSetShapePropertiesMutationData } from '../base/valid-mutation-data';

export class SetShapePropertiesMutation extends BaseMutation<
    PresentationDataModel,
    IValidSetShapePropertiesMutationData
> {
    public readonly type: MutationType = MutationType.SetShapeProperties;

    public apply(presentation: PresentationDataModel) {
        const { shapeId, shapeDelta } = this.data;
        const shape = presentation.store.models.get(shapeId, ...SHAPE_DATA_TYPES);
        assert(shape, 'shape should exist');

        const overrideProps = { nvPr: { addon: true, attachment: true }, style: true } as const;

        if (shape.type === DataType.Shape) {
            shape.store.patchData(ShapeDeltaUtils.toShapeDataDelta(shapeDelta), overrideProps);
        }

        if (shape.type === DataType.GroupShape) {
            shape.store.patchData(ShapeDeltaUtils.toGroupShapeDataDelta(shapeDelta), overrideProps);
        }

        if (shape.type === DataType.Connector) {
            shape.store.patchData(ShapeDeltaUtils.toConnectorDataDelta(shapeDelta), overrideProps);
        }

        if (shape.type === DataType.Picture) {
            shape.store.patchData(ShapeDeltaUtils.toPictureDataDelta(shapeDelta), overrideProps);
        }

        if (shape.type === DataType.GraphicalObjectFrameTable) {
            shape.store.patchData(ShapeDeltaUtils.toTableDataDelta(shapeDelta), {
                ...overrideProps,
                graphic: { graphicData: { tbl: { tblPr: true } } },
            });
        }

        if (shape.type === DataType.GraphicalObjectFrameChart) {
            shape.store.patchData(ShapeDeltaUtils.toGraphicObjectFrameDataDelta(shapeDelta), overrideProps);
        }

        if (shape.type === DataType.GraphicalObjectFrameSmartArt) {
            shape.store.patchData(ShapeDeltaUtils.toGraphicObjectFrameDataDelta(shapeDelta));
        }
    }

    public invert(presentation: PresentationDataModel): [IValidSetShapePropertiesMutationData] | [] {
        const { pageId, shapeId } = this.data;
        const shape = presentation.store.models.get(shapeId, ...SHAPE_DATA_TYPES);
        assert(shape, 'shape should exist');

        const setShapeProperties = {
            type: MutationType.SetShapeProperties,
            pageId,
            shapeId,
        };

        if (shape.type === DataType.Shape) {
            return [
                {
                    ...setShapeProperties,
                    shapeDelta: invert(
                        ShapeDeltaUtils.fromShapeData(shape.store.data),
                        ShapeDeltaUtils.fromMutationData(this.data),
                    ),
                },
            ];
        }

        if (shape.type === DataType.GroupShape) {
            return [
                {
                    ...setShapeProperties,
                    shapeDelta: invert(
                        ShapeDeltaUtils.fromGroupShapeData(shape.store.data),
                        ShapeDeltaUtils.fromMutationData(this.data),
                    ),
                },
            ];
        }

        if (shape.type === DataType.Connector) {
            return [
                {
                    ...setShapeProperties,
                    shapeDelta: invert(
                        ShapeDeltaUtils.fromConnectorData(shape.store.data),
                        ShapeDeltaUtils.fromMutationData(this.data),
                    ),
                },
            ];
        }

        if (shape.type === DataType.Picture) {
            return [
                {
                    ...setShapeProperties,
                    shapeDelta: invert(
                        ShapeDeltaUtils.fromPictureData(shape.store.data),
                        ShapeDeltaUtils.fromMutationData(this.data),
                    ),
                },
            ];
        }

        if (shape.type === DataType.GraphicalObjectFrameTable) {
            return [
                {
                    ...setShapeProperties,
                    shapeDelta: invert(
                        ShapeDeltaUtils.fromTableData(shape.store.data),
                        ShapeDeltaUtils.fromMutationData(this.data),
                    ),
                },
            ];
        }

        if (shape.type === DataType.GraphicalObjectFrameChart) {
            return [
                {
                    ...setShapeProperties,
                    shapeDelta: invert(
                        ShapeDeltaUtils.fromGraphicObjectFrameData(shape.store.data),
                        ShapeDeltaUtils.fromMutationData(this.data),
                    ),
                },
            ];
        }

        if (shape.type === DataType.GraphicalObjectFrameSmartArt) {
            return [
                {
                    ...setShapeProperties,
                    shapeDelta: invert(
                        ShapeDeltaUtils.fromGraphicObjectFrameData(shape.store.data),
                        ShapeDeltaUtils.fromMutationData(this.data),
                    ),
                },
            ];
        }

        return [];
    }

    public check(presentation: PresentationDataModel): MaybeError {
        return Rule.shape.should.existInPage(presentation, this.data.shapeId, this.data.pageId);
    }
}
